import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { userService, type CreateUserDTO, type UserFilters, type ImportResult } from '@/services/userService';
import { compagnieService } from '@/services/compagnieService';
import { qualificationService } from '@/services/qualificationService';
import type { User } from '@/types';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  KeyIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  DocumentArrowDownIcon,
  ShieldExclamationIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

// SFD v2.1: Roles disponibles
const ROLES = {
  ROLE_ADMIN: 'Administrateur',
  ROLE_GID_REGUL: 'Regulateur GID',
  ROLE_REGULATEUR: 'Regulateur (Module Regulation)',
  ROLE_CHEF_DENEIGNEMENT: "Chef d'equipe",
  ROLE_AGENT_DENEIGNEMENT: 'Agent de deneignement',
  ROLE_COMPAGNIE: 'Compagnie aerienne',
};

const ROLE_COLORS: Record<string, string> = {
  ROLE_ADMIN: 'bg-red-100 text-red-800',
  ROLE_GID_REGUL: 'bg-blue-100 text-blue-800',
  ROLE_REGULATEUR: 'bg-indigo-100 text-indigo-800',
  ROLE_CHEF_DENEIGNEMENT: 'bg-purple-100 text-purple-800',
  ROLE_AGENT_DENEIGNEMENT: 'bg-green-100 text-green-800',
  ROLE_COMPAGNIE: 'bg-orange-100 text-orange-800',
};

export function UsersPage() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<UserFilters>({});
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingUser, setEditingUser] = useState<number | null>(null);
  const [formData, setFormData] = useState<CreateUserDTO>({
    email: '',
    nom: '',
    prenom: '',
    password: '',
    roles: ['ROLE_AGENT_DENEIGNEMENT'],
    telephone: '',
    actif: true,
    compagnieIds: [],
    autoriseCreationIntervention: false,
  });
  const [newPassword, setNewPassword] = useState('');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importRole, setImportRole] = useState('ROLE_AGENT_DENEIGNEMENT');
  const [importPassword, setImportPassword] = useState('agent123');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Chargement des utilisateurs
  const { data: users, isLoading } = useQuery({
    queryKey: ['users', filters],
    queryFn: () => userService.list(filters),
  });

  // Compagnies disponibles (pour le multi-select ROLE_COMPAGNIE)
  const { data: compagnies } = useQuery({
    queryKey: ['compagnies', 'all'],
    queryFn: () => compagnieService.list(),
  });

  // Qualifications disponibles (pour le multi-select ROLE_AGENT_DENEIGNEMENT)
  const { data: qualificationsDisponibles } = useQuery({
    queryKey: ['qualifications'],
    queryFn: () => qualificationService.list(),
  });
  const [qualificationIds, setQualificationIds] = useState<number[]>([]);

  // Sauvegarde des qualifications d'un agent (endpoint distinct de
  // CreateUserDTO/UpdateUserDTO - cf qualificationService).
  const saveQualifications = async (agentId: number) => {
    await qualificationService.updateAgentQualifications(
      agentId,
      qualificationIds.map((id) => ({ qualificationId: id }))
    );
    queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateUserDTO) => userService.create(data),
    onSuccess: async (user) => {
      if (user.roles.includes('ROLE_AGENT_DENEIGNEMENT')) {
        await saveQualifications(user.id);
      }
      queryClient.invalidateQueries({ queryKey: ['users'] });
      closeModal();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateUserDTO> }) =>
      userService.update(id, data),
    onSuccess: async (user) => {
      if (user.roles.includes('ROLE_AGENT_DENEIGNEMENT')) {
        await saveQualifications(user.id);
      }
      queryClient.invalidateQueries({ queryKey: ['users'] });
      closeModal();
    },
  });

  const toggleActifMutation = useMutation({
    mutationFn: (id: number) => userService.toggleActif(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: ({ id, password }: { id: number; password: string }) =>
      userService.changePassword(id, password),
    onSuccess: () => {
      setShowPasswordModal(false);
      setEditingUser(null);
      setNewPassword('');
    },
  });

  const reset2FAMutation = useMutation({
    mutationFn: (id: number) => userService.reset2FA(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('2FA reinitalisee avec succes');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Erreur lors de la reinitialisation de la 2FA');
    },
  });

  const handleReset2FA = (userId: number, userName: string) => {
    if (window.confirm(`Voulez-vous vraiment reinitialiser la 2FA de ${userName} ? L'utilisateur devra reconfigurer son authentification a deux facteurs.`)) {
      reset2FAMutation.mutate(userId);
    }
  };

  const importMutation = useMutation({
    mutationFn: () => userService.importFromFile(importFile!, importRole, importPassword),
    onSuccess: (result) => {
      setImportResult(result);
      if (result.imported > 0) {
        queryClient.invalidateQueries({ queryKey: ['users'] });
        toast.success(`${result.imported} agent(s) importe(s)`);
      }
      if (result.errors.length > 0) {
        toast.error(`${result.errors.length} erreur(s) lors de l'import`);
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Erreur lors de l\'import');
    },
  });

  const downloadTemplate = async () => {
    try {
      const blob = await userService.downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'modele_import_agents.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Modele telecharge');
    } catch {
      toast.error('Erreur lors du telechargement');
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportResult(null);
    setImportRole('ROLE_AGENT_DENEIGNEMENT');
    setImportPassword('agent123');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImport = (e: React.FormEvent) => {
    e.preventDefault();
    if (importFile) {
      importMutation.mutate();
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      email: '',
      nom: '',
      prenom: '',
      password: '',
      roles: ['ROLE_AGENT_DENEIGNEMENT'],
      telephone: '',
      actif: true,
      compagnieIds: [],
      autoriseCreationIntervention: false,
    });
    setQualificationIds([]);
  };

  const openEditModal = (user: User) => {
    setEditingUser(user.id);
    setFormData({
      email: user.email,
      nom: user.nom,
      prenom: user.prenom,
      password: '',
      roles: user.roles.filter((r: string) => r !== 'ROLE_USER'),
      telephone: user.telephone || '',
      actif: user.actif,
      compagnieIds: (user.compagnies || []).map((c) => c.id),
      autoriseCreationIntervention: user.autoriseCreationIntervention || false,
    });
    setQualificationIds((user.qualifications || []).map((q) => q.qualification.id));
    setShowModal(true);
  };

  const toggleCompagnieId = (compagnieId: number) => {
    setFormData((prev) => {
      const current = prev.compagnieIds || [];
      const next = current.includes(compagnieId)
        ? current.filter((id) => id !== compagnieId)
        : [...current, compagnieId];
      return { ...prev, compagnieIds: next };
    });
  };

  const toggleQualificationId = (qualificationId: number) => {
    setQualificationIds((prev) =>
      prev.includes(qualificationId)
        ? prev.filter((id) => id !== qualificationId)
        : [...prev, qualificationId]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingUser) {
      const { password, ...data } = formData;
      updateMutation.mutate({ id: editingUser, data: password ? formData : data });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handlePasswordChange = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingUser && newPassword) {
      changePasswordMutation.mutate({ id: editingUser, password: newPassword });
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
        <p className="text-sm text-gray-500 mt-3">Chargement des utilisateurs...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Gestion des utilisateurs</h2>
        <div className="flex items-center space-x-3">
          <button onClick={() => setShowImportModal(true)} className="btn-secondary">
            <ArrowUpTrayIcon className="w-5 h-5 mr-2" />
            Importer
          </button>
          <button onClick={() => setShowModal(true)} className="btn-primary">
            <PlusIcon className="w-5 h-5 mr-2" />
            Nouvel utilisateur
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="label">Recherche</label>
              <div className="relative">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  className="input pl-10"
                  placeholder="Nom, prenom, email..."
                  value={filters.search || ''}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value || undefined })}
                />
              </div>
            </div>
            <div>
              <label className="label">Role</label>
              <select
                className="input"
                value={filters.role || ''}
                onChange={(e) => setFilters({ ...filters, role: e.target.value || undefined })}
              >
                <option value="">Tous les roles</option>
                {Object.entries(ROLES).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Statut</label>
              <select
                className="input"
                value={filters.actif === undefined ? '' : String(filters.actif)}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    actif: e.target.value === '' ? undefined : e.target.value === 'true',
                  })
                }
              >
                <option value="">Tous</option>
                <option value="true">Actifs</option>
                <option value="false">Inactifs</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setFilters({})}
                className="btn-secondary w-full"
              >
                Reinitialiser
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tableau */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Email</th>
                <th>Telephone</th>
                <th>Roles</th>
                <th>2FA</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users?.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500">
                    Aucun utilisateur trouve
                  </td>
                </tr>
              ) : (
                users?.map((user) => (
                  <tr key={user.id}>
                    <td className="font-medium">
                      {user.prenom} {user.nom}
                    </td>
                    <td>{user.email}</td>
                    <td>{user.telephone || '-'}</td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {user.roles
                          .filter((r: string) => r !== 'ROLE_USER')
                          .map((role: string) => (
                            <span
                              key={role}
                              className={`px-2 py-0.5 text-xs rounded-full ${ROLE_COLORS[role] || 'bg-gray-100 text-gray-800'}`}
                            >
                              {ROLES[role as keyof typeof ROLES] || role}
                            </span>
                          ))}
                      </div>
                      {user.roles.includes('ROLE_COMPAGNIE') && (
                        <div className="text-xs text-gray-500 mt-1">
                          {user.compagnies && user.compagnies.length > 0
                            ? user.compagnies.map((c) => c.codeIata).join(', ')
                            : <span className="text-red-500">Aucune compagnie - rien de visible</span>}
                          {user.autoriseCreationIntervention && (
                            <span className="ml-1 text-green-600">(peut creer)</span>
                          )}
                        </div>
                      )}
                      {user.roles.includes('ROLE_AGENT_DENEIGNEMENT') && (
                        <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-1">
                          {user.qualifications && user.qualifications.length > 0
                            ? user.qualifications.map((q) => (
                                <span
                                  key={q.id}
                                  className={q.expiree ? 'text-red-500' : undefined}
                                  title={q.expiree ? 'Qualification expiree' : undefined}
                                >
                                  {q.qualification.libelle}
                                  {q.expiree ? ' (expiree)' : ''}
                                </span>
                              ))
                            : <span className="text-amber-600">Aucune qualification</span>}
                        </div>
                      )}
                    </td>
                    <td>
                      {user.isTotpEnabled ? (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">
                          <ShieldCheckIcon className="w-3.5 h-3.5 mr-1" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td>
                      <button
                        onClick={() => toggleActifMutation.mutate(user.id)}
                        className={`flex items-center ${user.actif ? 'text-green-600' : 'text-red-600'}`}
                      >
                        {user.actif ? (
                          <>
                            <CheckCircleIcon className="w-5 h-5 mr-1" />
                            Actif
                          </>
                        ) : (
                          <>
                            <XCircleIcon className="w-5 h-5 mr-1" />
                            Inactif
                          </>
                        )}
                      </button>
                    </td>
                    <td>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openEditModal(user)}
                          className="p-1 text-gray-400 hover:text-primary-500"
                          title="Modifier"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => {
                            setEditingUser(user.id);
                            setShowPasswordModal(true);
                          }}
                          className="p-1 text-gray-400 hover:text-primary-500"
                          title="Changer mot de passe"
                        >
                          <KeyIcon className="w-5 h-5" />
                        </button>
                        {user.isTotpEnabled && (
                          <button
                            onClick={() => handleReset2FA(user.id, `${user.prenom} ${user.nom}`)}
                            className="p-1 text-gray-400 hover:text-orange-500"
                            title="Reinitialiser 2FA"
                            disabled={reset2FAMutation.isPending}
                          >
                            <ShieldExclamationIcon className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => toggleActifMutation.mutate(user.id)}
                          className="p-1 text-gray-400 hover:text-red-500"
                          title={user.actif ? 'Desactiver' : 'Activer'}
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Creation/Edition */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">
                {editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
              </h3>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="px-6 py-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Prenom *</label>
                    <input
                      type="text"
                      className="input"
                      value={formData.prenom}
                      onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="label">Nom *</label>
                    <input
                      type="text"
                      className="input"
                      value={formData.nom}
                      onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="label">Email *</label>
                  <input
                    type="email"
                    className="input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="label">Telephone</label>
                  <input
                    type="tel"
                    className="input"
                    value={formData.telephone}
                    onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                  />
                </div>
                <div>
                  <label className="label">
                    Mot de passe {editingUser ? '(laisser vide pour ne pas changer)' : '*'}
                  </label>
                  <input
                    type="password"
                    className="input"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingUser}
                    minLength={6}
                  />
                </div>
                <div>
                  <label className="label">Role *</label>
                  <select
                    className="input"
                    value={formData.roles?.[0] || 'ROLE_AGENT_DENEIGNEMENT'}
                    onChange={(e) => setFormData({ ...formData, roles: [e.target.value] })}
                  >
                    {Object.entries(ROLES).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                {formData.roles?.[0] === 'ROLE_COMPAGNIE' && (
                  <div>
                    <label className="label">Compagnies visibles *</label>
                    <p className="text-xs text-gray-500 mb-2">
                      Cet utilisateur ne verra que les interventions des compagnies cochees.
                      Aucune compagnie cochee = aucune donnee visible.
                    </p>
                    <div className="border border-gray-300 rounded-lg max-h-40 overflow-y-auto divide-y divide-gray-100">
                      {(compagnies || []).map((c) => (
                        <label
                          key={c.id}
                          className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={(formData.compagnieIds || []).includes(c.id)}
                            onChange={() => toggleCompagnieId(c.id)}
                          />
                          {c.codeIata} - {c.nom}
                        </label>
                      ))}
                    </div>
                    <label className="flex items-center gap-2 text-sm mt-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.autoriseCreationIntervention || false}
                        onChange={(e) =>
                          setFormData({ ...formData, autoriseCreationIntervention: e.target.checked })
                        }
                      />
                      Autoriser cet utilisateur a creer une demande de deneigement
                    </label>
                  </div>
                )}
                {formData.roles?.[0] === 'ROLE_AGENT_DENEIGNEMENT' && (
                  <div>
                    <label className="label">Qualifications de conduite</label>
                    <p className="text-xs text-gray-500 mb-2">
                      Conditionne les vehicules que cet agent peut conduire (RG-VH-07).
                    </p>
                    <div className="border border-gray-300 rounded-lg max-h-40 overflow-y-auto divide-y divide-gray-100">
                      {(qualificationsDisponibles || []).map((q) => (
                        <label
                          key={q.id}
                          className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={qualificationIds.includes(q.id)}
                            onChange={() => toggleQualificationId(q.id)}
                          />
                          {q.libelle}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="px-6 py-4 border-t flex justify-end space-x-3">
                <button type="button" onClick={closeModal} className="btn-secondary">
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {editingUser ? 'Modifier' : 'Creer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Changement mot de passe */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">Changer le mot de passe</h3>
            </div>
            <form onSubmit={handlePasswordChange}>
              <div className="px-6 py-4">
                <label className="label">Nouveau mot de passe *</label>
                <input
                  type="password"
                  className="input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="Minimum 6 caracteres"
                />
              </div>
              <div className="px-6 py-4 border-t flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setNewPassword('');
                  }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={changePasswordMutation.isPending}
                >
                  Changer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Import */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="px-6 py-4 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Importer des agents</h3>
                <Link
                  to="/administration/imports"
                  className="flex items-center text-sm text-gray-500 hover:text-primary-600"
                  title="Voir l'historique complet des imports"
                >
                  Historique des imports
                </Link>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Importez une liste d'agents depuis un fichier Excel (XLS, XLSX) ou CSV
              </p>
            </div>
            <form onSubmit={handleImport}>
              <div className="px-6 py-4 space-y-4">
                {/* Telecharger modele */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-900">Modele de fichier</p>
                      <p className="text-xs text-blue-700">Format: Nom | Prenom | Email | Telephone</p>
                    </div>
                    <button
                      type="button"
                      onClick={downloadTemplate}
                      className="flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                    >
                      <DocumentArrowDownIcon className="w-4 h-4 mr-1" />
                      Telecharger
                    </button>
                  </div>
                </div>

                {/* Selection fichier */}
                <div>
                  <label className="label">Fichier a importer *</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xls,.xlsx,.csv"
                    className="input"
                    onChange={(e) => {
                      setImportFile(e.target.files?.[0] || null);
                      setImportResult(null);
                    }}
                    required
                  />
                </div>

                {/* Role */}
                <div>
                  <label className="label">Role a attribuer</label>
                  <select
                    className="input"
                    value={importRole}
                    onChange={(e) => setImportRole(e.target.value)}
                  >
                    {Object.entries(ROLES).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Mot de passe par defaut */}
                <div>
                  <label className="label">Mot de passe par defaut</label>
                  <input
                    type="text"
                    className="input"
                    value={importPassword}
                    onChange={(e) => setImportPassword(e.target.value)}
                    placeholder="agent123"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Ce mot de passe sera attribue a tous les agents importes
                  </p>
                </div>

                {/* Resultat de l'import */}
                {importResult && (
                  <div className={`p-4 rounded-lg ${importResult.errors.length > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
                    <p className="font-medium text-sm">
                      {importResult.imported} agent(s) importe(s), {importResult.skipped} ignore(s)
                    </p>
                    {importResult.errors.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-amber-800 font-medium">Erreurs:</p>
                        <ul className="text-xs text-amber-700 list-disc list-inside max-h-32 overflow-y-auto">
                          {importResult.errors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="px-6 py-4 border-t flex justify-end space-x-3">
                <button type="button" onClick={closeImportModal} className="btn-secondary">
                  {importResult ? 'Fermer' : 'Annuler'}
                </button>
                {!importResult && (
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={!importFile || importMutation.isPending}
                  >
                    {importMutation.isPending ? 'Import en cours...' : 'Importer'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsersPage;
